import { useCallback, useSyncExternalStore } from 'react';
import useSWR from 'swr';

import { dateQueryFor } from 'api/metricQuery';
import { useTenant } from 'hooks';

import { useInsightFiltersContext } from '../filters/InsightFiltersContext';
import {
  CardDataState,
  IntelligenceFilters,
  IntelligenceFixture,
  IProductScopedFilters
} from '../types';
import { cacheKeyFor, getCardCacheVersion, isCardRevoked, subscribeToCardCache } from './cardCache';
import { fetchCardLive } from './liveCardSource';

// The single data seam for every Customer Insights card. Consumers only ever see a data-fetching
// interface, so what answers is decided here and nowhere else. Card bodies branch on `state`, not on
// `isLoading`: the FR's seven data states are a property of the payload and `isLoading`/`error`
// cannot express them.
//
// The API is the only source. There is deliberately NO fallback to the fixtures when a request
// fails: a fixture number appearing because the endpoint returned an error is indistinguishable from
// a measurement, which is the fabricated-data failure this whole seam exists to prevent. A failed
// card says it failed. PLA-8815.
export interface UseCardDataResult {
  data: IntelligenceFixture | undefined;
  isLoading: boolean;
  error: Error | undefined;
  state: CardDataState;
  mutate: () => void;
}

const TECHNICAL_ERROR_STATE: CardDataState = { kind: 'technicalError' };
const PERMISSION_RESTRICTED_STATE: CardDataState = { kind: 'permissionRestricted' };
const LOADING_STATE: CardDataState = { kind: 'loading' };

export const useCardData = (
  cardKey: string,
  filters?: IntelligenceFilters,
  // The authorisation answer, threaded in so the gate PRECEDES the request: a card the role cannot
  // view must not be fetched at all, not fetched and then swept up afterwards
  // (`conventions/permissions/gotchas.md § 2`). It is SWR's null key below, so an unpermitted card
  // costs no network call rather than costing one whose result is discarded.
  enabled = true,
  // The viewport answer (`hooks/useInViewport`), same shape as `enabled` but a different reason: not
  // yet on screen is not a fault and not a denial, so it renders as `loading`, never as
  // `technicalError` or `permissionRestricted`. Defaults to visible so every existing caller that
  // does not pass this — every test, every card not yet wired to a viewport gate — fetches exactly
  // as it did before this argument existed. PLA-8815.
  isVisible = true
): UseCardDataResult => {
  const { name: tenantName } = useTenant();
  // The screen's filter panel publishes through context (SP-1), so a card picks the active filters
  // up without any section passing them down. The explicit argument is merged over them rather than
  // replacing them: Product Readiness keys its read on the selected product, which is a card-local
  // parameter that has to compose with the panel's, not silently drop it. Outside a provider this
  // resolves to today's default window rather than throwing, same as `useActiveInsightFilters` did.
  const { filters: activeFilters, dateRange, comparisonRange } = useInsightFiltersContext();
  const mergedFilters: Partial<IProductScopedFilters> | undefined =
    activeFilters || filters ? { ...activeFilters, ...filters } : undefined;
  const cacheKey = cacheKeyFor(mergedFilters, tenantName);
  // Dates go on every request; the category vocabulary stays off the wire until
  // `METRIC_QUERY_ENABLED` (`api/metricQuery.ts`) — see that file for why.
  const query = dateQueryFor(dateRange, comparisonRange);

  // Revocation still runs through `cardCache`: it is the channel `useCardVisibility` drops a card
  // down when a grant disappears mid-session, and subscribing is what makes that drop reach a
  // consumer that is already mounted.
  useSyncExternalStore(subscribeToCardCache, getCardCacheVersion);

  // A revoked card is empty by authorisation, not by fault, so it must not surface as a failed read.
  const revoked = !enabled || isCardRevoked(cardKey);

  // The tenant is in the key because `/v4/dash` reads are tenant-scoped and an in-SPA tenant switch
  // never reloads the page — without it one tenant's payload would be served to the next. `query`
  // is also in the key so a resolved date window is never handed back for a different one; the
  // selected preset ID is already inside `cacheKey` via `activeFilters.dateRange`, but the concrete
  // dates it resolves to are not, so they ride separately here.
  const {
    data: assembly,
    isLoading: isFetching,
    mutate: revalidate
  } = useSWR(
    !revoked && isVisible && tenantName
      ? { scope: 'customer-insights-card', cardKey, tenantName, cacheKey, query }
      : null,
    // The product is read off the SAME merged object the cache key is built from, never from a
    // second source: Product Readiness resolves its catalogue metric from the selection
    // (`registry/metricIdentifiers.ts` § `productReadinessIdentitiesFor`), so a scope that could
    // disagree with the key would serve one product's reading under another product's entry.
    () => fetchCardLive(cardKey, query, { product: mergedFilters?.product }, tenantName)
  );

  const mutate = useCallback(() => {
    revalidate();
  }, [revalidate]);

  if (revoked) {
    return {
      data: undefined,
      isLoading: false,
      error: undefined,
      state: PERMISSION_RESTRICTED_STATE,
      mutate
    };
  }

  // Not yet on screen: no request has been issued (the SWR key above is null), so there is no
  // `assembly` to read a state off. The card draws its skeleton until the viewport gate opens,
  // exactly as it would while a real request was in flight.
  if (!isVisible) {
    return {
      data: undefined,
      isLoading: true,
      error: undefined,
      state: LOADING_STATE,
      mutate
    };
  }

  return {
    data: assembly?.fixture,
    isLoading: isFetching,
    // A fetch in flight is not a failure, and neither is one that resolved into a data state:
    // `fetchMetric` never rejects, so every outcome arrives as something to render rather than as an
    // exception. `error` stays undefined and the card branches on `state`.
    error: undefined,
    state: assembly?.fixture.state ?? (isFetching ? LOADING_STATE : TECHNICAL_ERROR_STATE),
    mutate
  };
};

export default useCardData;
